import React, { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay } from "@fortawesome/free-solid-svg-icons";
import motosierraHero from "../assets/images/brand/hero-img.jpg";
import axios from "axios";
import "../styles/videos-page.css";

const ApiURL = process.env.REACT_APP_API_URL;

const CATEGORIES = ["Todos", "Mantenimiento", "Uso", "Novedades"];

const BADGE_CLASS = {
  Mantenimiento: "badge-mant",
  Uso: "badge-uso",
  Novedades: "badge-nov",
};

const VideoCard = ({ video, isActive, onClick }) => {
  const [imgSrc, setImgSrc] = useState(
    `https://i.ytimg.com/vi/${video.youtubeId}/maxresdefault.jpg`,
  );

  const tryNextQuality = () => {
    if (imgSrc.includes("maxresdefault")) {
      setImgSrc(`https://i.ytimg.com/vi/${video.youtubeId}/sddefault.jpg`);
    } else if (imgSrc.includes("sddefault")) {
      setImgSrc(`https://i.ytimg.com/vi/${video.youtubeId}/hqdefault.jpg`);
    }
  };

  const handleLoad = (e) => {
    if (e.target.naturalWidth === 120) tryNextQuality();
  };

  return (
    <div
      className={`vcard${isActive ? " vcard-active" : ""}`}
      onClick={onClick}
    >
      <div className="vcard-thumb">
        <img
          src={imgSrc}
          alt={video.title}
          loading="lazy"
          onError={tryNextQuality}
          onLoad={handleLoad}
        />
        <div className="vcard-overlay">
          <div className="vcard-play-btn">
            <FontAwesomeIcon icon={faPlay} />
          </div>
        </div>
        <span className={`vcard-badge ${BADGE_CLASS[video.category] || ""}`}>
          {video.category}
        </span>
      </div>
      <div className="vcard-body">
        <div className="vcard-source">STIHL Argentina</div>
        <h3 className="vcard-title">{video.title}</h3>
        <div className="vcard-footer">
          <span className="vcard-tag">{video.tags?.[0]}</span>
          {isActive && <span className="vcard-playing">Reproduciendo</span>}
        </div>
      </div>
    </div>
  );
};

const VideosPage = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("Todos");
  const [currentVideo, setCurrentVideo] = useState(null);
  const [playing, setPlaying] = useState(false);
  const playerRef = useRef(null);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const res = await axios.get(`${ApiURL}/videos`);
        setVideos(res.data);
        const featured = res.data.find((v) => v.featured) || res.data[0];
        setCurrentVideo(featured);
      } catch (error) {
        console.error("Error al obtener videos:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();
  }, []);

  const rest = videos.filter((v) => !v.featured);

  const filtered =
    activeCategory === "Todos"
      ? rest
      : rest.filter((v) => v.category === activeCategory);

  const scrollToPlayer = () => {
    if (!playerRef.current) return;
    const navbarH = window.innerWidth <= 960 ? 70 : 100;
    const current = document.documentElement.scrollTop || document.body.scrollTop;
    const target = playerRef.current.getBoundingClientRect().top + current - navbarH;
    document.documentElement.scrollTop = target;
    document.body.scrollTop = target;
  };

  const handleSelectVideo = (video) => {
    setCurrentVideo(video);
    setPlaying(true);
    setTimeout(scrollToPlayer, 50);
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!currentVideo) {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <p style={{ color: "#888" }}>No hay videos disponibles.</p>
      </div>
    );
  }

  return (
    <div className="videos-page">
      <Helmet>
        <title>Aprendé | Cáceres Maquinarias</title>
        <meta
          name="description"
          content="Tutoriales y consejos de uso, mantenimiento y seguridad de herramientas STIHL. Canal oficial STIHL Argentina."
        />
        <meta property="og:title" content="Tutoriales | Cáceres Maquinarias" />
        <meta
          property="og:description"
          content="Tutoriales y consejos de uso, mantenimiento y seguridad de herramientas STIHL."
        />
        <meta
          property="og:url"
          content="https://www.caceresmaquinarias.com.ar/videos"
        />
        <meta
          property="og:image"
          content="https://www.caceresmaquinarias.com.ar/Logo-CM.png"
        />
      </Helmet>

      <section className="videos-hero">
        <div
          className="videos-hero-photo"
          style={{ backgroundImage: `url(${motosierraHero})` }}
        />
        <div className="videos-hero-overlay" />

        <div className="videos-hero-content">
          <div>
            <div className="videos-hero-eyebrow">
              <span className="videos-hero-dot" />
              Cáceres Maquinarias — Concesionario Oficial
            </div>
            <h1 className="videos-hero-title">
              Tips, guías
              <br />
              <em>y tutoriales</em>
            </h1>
            <p className="videos-hero-sub">
              Todo lo que necesitás saber sobre tus máquinas: mantenimiento, uso
              correcto y consejos directos de los{" "}
              <strong>expertos STIHL</strong>.
            </p>
            <a
              href="#videos-player"
              className="videos-hero-cta"
              onClick={(e) => { e.preventDefault(); scrollToPlayer(); }}
            >
              <svg viewBox="0 0 24 24">
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
              Ver tutoriales
            </a>
          </div>
        </div>

        <a
          className="videos-hero-scroll"
          href="#videos-player"
          onClick={(e) => { e.preventDefault(); scrollToPlayer(); }}
        >
          <span className="videos-hero-scroll-text">Seguí aprendiendo</span>
          <svg viewBox="0 0 24 24">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </a>
      </section>

      <div id="videos-player" className="videos-section" ref={playerRef}>
        <div className="videos-section-label-row">
          <div className="videos-section-label">Reproduciendo ahora</div>
        </div>
        <div className="videos-featured-card">
          <div className="videos-featured-video">
            {!playing ? (
              <div className="videos-fv-thumb" onClick={() => setPlaying(true)}>
                <img
                  src={`https://img.youtube.com/vi/${currentVideo.youtubeId}/maxresdefault.jpg`}
                  alt={currentVideo.title}
                />
                <div className="videos-fv-play">
                  <FontAwesomeIcon icon={faPlay} />
                </div>
              </div>
            ) : (
              <iframe
                src={`https://www.youtube.com/embed/${currentVideo.youtubeId}?autoplay=1&rel=0`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={currentVideo.title}
              />
            )}
          </div>
          <div className="videos-featured-info">
            <div className="videos-feat-main">
              <div className="videos-feat-badge">
                {currentVideo.featured
                  ? "Destacado del mes"
                  : currentVideo.category}
              </div>
              <h2 className="videos-feat-title">{currentVideo.title}</h2>
              {currentVideo.description && (
                <p className="videos-feat-desc">{currentVideo.description}</p>
              )}
              <div className="videos-feat-tags">
                {currentVideo.tags?.map((t) => (
                  <span key={t} className="videos-vtag">
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {videos
              .filter(
                (v) =>
                  v._id !== currentVideo._id &&
                  v.category === currentVideo.category,
              )
              .slice(0, 3).length > 0 && (
              <div className="videos-related">
                <div className="videos-related-label">
                  Más de {currentVideo.category}
                </div>
                <div className="videos-related-list">
                  {videos
                    .filter(
                      (v) =>
                        v._id !== currentVideo._id &&
                        v.category === currentVideo.category,
                    )
                    .slice(0, 3)
                    .map((v) => (
                      <div
                        key={v._id}
                        className="videos-related-item"
                        onClick={() => handleSelectVideo(v)}
                      >
                        <div className="videos-related-thumb">
                          <img
                            src={`https://i.ytimg.com/vi/${v.youtubeId}/mqdefault.jpg`}
                            alt={v.title}
                          />
                          <div className="videos-related-play">
                            <FontAwesomeIcon icon={faPlay} />
                          </div>
                        </div>
                        <span className="videos-related-title">{v.title}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="videos-section" style={{ paddingTop: 0 }}>
        <div className="videos-section-label-row">
          <div className="videos-section-label">
            {activeCategory === "Todos" ? "Todos los videos" : activeCategory}
          </div>
          <div className="videos-filters">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                className={`videos-filter-btn${activeCategory === cat ? " active" : ""}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
        {filtered.length === 0 ? (
          <p className="videos-empty">
            No hay videos en esta categoría todavía.
          </p>
        ) : (
          <div className="videos-grid">
            {filtered.map((v) => (
              <VideoCard
                key={v._id}
                video={v}
                isActive={currentVideo._id === v._id}
                onClick={() => handleSelectVideo(v)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="videos-cta">
        <div className="videos-cta-inner">
          <div className="videos-cta-label">¿Tenés dudas?</div>
          <div className="videos-cta-title">
            Nuestro equipo está para ayudarte
          </div>
          <p className="videos-cta-desc">
            Si después de ver los tutoriales todavía tenés preguntas sobre tus
            máquinas, pasate por el local o escribinos. Estamos para asesorarte.
          </p>
          <a
            href="https://wa.me/5493464540919?text=Hola,%20tengo%20una%20consulta%20sobre%20una%20máquina%20STIHL"
            target="_blank"
            rel="noopener noreferrer"
            className="videos-cta-btn"
          >
            Escribinos por WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
};

export default VideosPage;
